import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Pencil, ShieldOff, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function todayIso() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [history, setHistory] = useState([]);
  const [retireOpen, setRetireOpen] = useState(false);
  const [retireForm, setRetireForm] = useState({
    retired_date: todayIso(),
    media_destroyed: false,
    media_destroyed_date: todayIso(),
    media_destruction_method: "",
    media_destruction_notes: "",
  });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/assets/${id}`).then(({ data }) => setAsset(data)).catch(() => {});
    api.get("/audit-logs", { params: { limit: 1000 } })
      .then(({ data }) => setHistory(data.filter((l) => l.asset_id === id)))
      .catch(() => {});
  };
  useEffect(() => { load(); }, [id]);

  const openRetire = () => {
    setRetireForm({
      retired_date: asset?.retired_date || todayIso(),
      media_destroyed: false,
      media_destroyed_date: todayIso(),
      media_destruction_method: "",
      media_destruction_notes: "",
    });
    setRetireOpen(true);
  };

  const onSubmitRetire = async () => {
    if (!retireForm.retired_date) { toast.error("Retired date is required"); return; }
    if (retireForm.media_destroyed && !retireForm.media_destroyed_date) {
      toast.error("Destruction date is required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/assets/${id}/retire`, retireForm);
      toast.success(retireForm.media_destroyed
        ? "Asset retired · media destruction recorded"
        : "Asset retired · media destruction NOT yet confirmed");
      setRetireOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to retire asset");
    } finally { setBusy(false); }
  };

  if (!asset) return <div className="p-8 font-mono text-xs text-[#7A7A7A]">Loading…</div>;

  const isRetired = asset.status === "retired";
  const mediaConfirmed = !!asset.media_destroyed;

  return (
    <div data-testid="asset-detail-page">
      <PageHeader
        eyebrow={`[ASSET / ${asset.asset_tag || "—"}]`}
        title={asset.name}
        subtitle={`${asset.manufacturer || ""} ${asset.model || ""}`.trim() || "—"}
        actions={
          <>
            <Link to="/assets" data-testid="back-link" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[#5A5A5A] hover:text-[#0F0F0F] mr-3">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </Link>
            {(!isRetired || !mediaConfirmed) && (
              <Button
                data-testid="retire-asset-btn"
                variant="outline"
                onClick={openRetire}
                className="rounded-none border-[#B45309] bg-transparent text-[#B45309] hover:bg-[#FFF4EE] hover:text-[#B45309] font-mono text-xs tracking-wider uppercase mr-2"
              >
                <ShieldOff className="w-3.5 h-3.5 mr-2" />
                {isRetired ? "Record Destruction" : "Retire Asset"}
              </Button>
            )}
            <Button
              data-testid="edit-asset-btn"
              onClick={() => navigate(`/assets/${id}/edit`)}
              className="rounded-none bg-[#F37B2A] hover:bg-[#D86A1F] text-white font-mono text-xs tracking-wider uppercase"
            >
              <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
            </Button>
          </>
        }
      />

      <div className="p-8 space-y-6">
        {isRetired && (
          <div
            data-testid="retirement-record"
            className={`border ${mediaConfirmed ? "border-[#2D7A4F]/40 bg-[#E8F1EB]" : "border-[#E0B8A5] bg-[#FFF4EE]"} px-6 py-5`}
          >
            <div className="flex items-start gap-3 mb-4">
              {mediaConfirmed ? (
                <ShieldCheck className="w-5 h-5 text-[#2D7A4F] mt-0.5" strokeWidth={1.75} />
              ) : (
                <AlertTriangle className="w-5 h-5 text-[#B45309] mt-0.5" strokeWidth={1.75} />
              )}
              <div>
                <div className={`uppercase-label ${mediaConfirmed ? "text-[#2D7A4F]" : "text-[#B45309]"}`}>
                  Retirement Record
                </div>
                <div className="text-sm text-[#3A3A3A] mt-0.5">
                  {mediaConfirmed
                    ? "This asset has been retired and its storage media has been securely destroyed. This record is retained for audit."
                    : "This asset is retired but storage media destruction has NOT yet been confirmed. Click Record Destruction above to complete the compliance record."}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 pl-8">
              <DetailRow label="Retired Date" value={asset.retired_date} mono />
              <DetailRow label="Retired By" value={asset.retired_by} mono />
              <DetailRow label="Recorded On" value={asset.retired_recorded_at ? new Date(asset.retired_recorded_at).toLocaleString() : "—"} mono />
              <DetailRow
                label="Media Destroyed"
                value={mediaConfirmed ? "Yes" : "Not confirmed"}
                mono
              />
              {mediaConfirmed && (
                <>
                  <DetailRow label="Destruction Date" value={asset.media_destroyed_date} mono />
                  <DetailRow label="Confirmed By" value={asset.media_destroyed_by} mono />
                  <DetailRow label="Method" value={asset.media_destruction_method || "—"} />
                  <DetailRow label="Confirmed On" value={asset.media_destroyed_recorded_at ? new Date(asset.media_destroyed_recorded_at).toLocaleString() : "—"} mono />
                </>
              )}
            </div>
            {mediaConfirmed && asset.media_destruction_notes && (
              <div className="mt-4 pl-8">
                <div className="uppercase-label">Destruction Notes</div>
                <div className="text-sm text-[#3A3A3A] mt-1 whitespace-pre-wrap">{asset.media_destruction_notes}</div>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 border border-[#E5E0D5] bg-white">
            <div className="px-6 py-3 border-b border-[#E5E0D5] flex items-center justify-between">
              <div className="uppercase-label">Record</div>
              <StatusPill status={asset.status} />
            </div>
            <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
              <DetailRow label="Asset Tag" value={asset.asset_tag} mono />
              <DetailRow label="Category" value={asset.category} />
              <DetailRow label="Serial Number" value={asset.serial_number} mono />
              <DetailRow label="Manufacturer" value={asset.manufacturer} />
              <DetailRow label="Model" value={asset.model} />
              <DetailRow label="Location" value={asset.location} />
              <DetailRow label="Operating System" value={asset.operating_system} />
              <DetailRow label="CPU" value={asset.cpu} mono />
              <DetailRow label="RAM" value={asset.ram} mono />
              <DetailRow label="HDD" value={asset.hdd} mono />
              <DetailRow label="Splashtop Name" value={asset.splashtop_name} mono />
              <DetailRow label="Assigned To" value={asset.assigned_to} />
              <DetailRow label="Assignee Email" value={asset.assigned_email} mono />
              <DetailRow label="Purchase Date" value={asset.purchase_date} mono />
              <DetailRow label="Warranty Expiry" value={asset.warranty_expiry} mono />
              <DetailRow label="Purchase Price" value={asset.purchase_cost ? `NZ$${Number(asset.purchase_cost).toLocaleString()}` : "—"} mono />
              <DetailRow label="Depreciation Type" value={asset.depreciation_type} />
              <DetailRow label="Depreciation Rate" value={asset.depreciation_rate ? `${asset.depreciation_rate}%` : "—"} mono />
              <DetailRow label="EOY1" value={asset.eoy1 ? `NZ$${Number(asset.eoy1).toLocaleString()}` : "—"} mono />
              <DetailRow label="EOY2" value={asset.eoy2 ? `NZ$${Number(asset.eoy2).toLocaleString()}` : "—"} mono />
              <DetailRow label="EOY3" value={asset.eoy3 ? `NZ$${Number(asset.eoy3).toLocaleString()}` : "—"} mono />
              <DetailRow label="EOY4" value={asset.eoy4 ? `NZ$${Number(asset.eoy4).toLocaleString()}` : "—"} mono />
              <DetailRow label="Last Updated" value={asset.updated_at ? new Date(asset.updated_at).toLocaleString() : "—"} mono />
            </div>
            {asset.notes && (
              <div className="px-6 pb-6">
                <div className="uppercase-label mb-2">Notes</div>
                <p className="text-sm text-[#3A3A3A] whitespace-pre-wrap">{asset.notes}</p>
              </div>
            )}
          </div>

          <div className="border border-[#E5E0D5] bg-white" data-testid="asset-history">
            <div className="px-6 py-3 border-b border-[#E5E0D5]">
              <div className="uppercase-label">History</div>
              <h3 className="text-base font-medium tracking-tight mt-1">Audit Trail</h3>
            </div>
            <div className="divide-y divide-[#E5E0D5] max-h-[600px] overflow-auto">
              {history.length === 0 && <div className="px-6 py-6 text-xs font-mono text-[#9A9A9A]">No events recorded.</div>}
              {history.map((h) => (
                <div key={h.id} className="px-6 py-3">
                  <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#7A7A7A]">
                    {new Date(h.timestamp).toLocaleString()}
                  </div>
                  <div className={`font-mono text-xs mt-0.5 ${h.action === "asset_retired" ? "text-[#B45309] font-medium" : "text-[#0F0F0F]"}`}>{h.action}</div>
                  {h.details && Object.keys(h.details).length > 0 && (
                    <div className="font-mono text-[11px] text-[#5A5A5A] mt-1 space-y-0.5">
                      {Object.entries(h.details).map(([k, v]) => (
                        <div key={k}>{k}: <span className="text-[#1F1F1F]">{String(v)}</span></div>
                      ))}
                    </div>
                  )}
                  <div className="font-mono text-[10px] text-[#9A9A9A] mt-1">by {h.user_email}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Retirement modal */}
      <AlertDialog open={retireOpen} onOpenChange={(o) => !o && setRetireOpen(false)}>
        <AlertDialogContent className="rounded-none bg-white border-[#D6CFC1] max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-medium tracking-tight">
              {isRetired ? "Record Media Destruction" : "Retire Asset"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#5A5A5A] text-sm">
              This information is stored on the asset and written to the audit log for compliance. It cannot be edited from the app once saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="uppercase-label">Retired Date *</Label>
              <Input
                data-testid="retire-date-input"
                type="date"
                value={retireForm.retired_date}
                onChange={(e) => setRetireForm({ ...retireForm, retired_date: e.target.value })}
                className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
              />
            </div>

            <div className="border border-[#E5E0D5] px-4 py-3 bg-[#FAF7F0]">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <Checkbox
                  data-testid="media-destroyed-checkbox"
                  checked={retireForm.media_destroyed}
                  onCheckedChange={(v) => setRetireForm({ ...retireForm, media_destroyed: !!v })}
                  className="mt-0.5 rounded-none border-[#D6CFC1] data-[state=checked]:bg-[#F37B2A]"
                />
                <span className="text-sm text-[#1F1F1F]">
                  I verify that the storage media has been <strong>securely destroyed</strong> (wiped, degaussed, shredded, or physically destroyed) and is unrecoverable.
                </span>
              </label>
            </div>

            {retireForm.media_destroyed && (
              <>
                <div>
                  <Label className="uppercase-label">Destruction Date *</Label>
                  <Input
                    data-testid="destruction-date-input"
                    type="date"
                    value={retireForm.media_destroyed_date}
                    onChange={(e) => setRetireForm({ ...retireForm, media_destroyed_date: e.target.value })}
                    className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
                  />
                </div>
                <div>
                  <Label className="uppercase-label">Destruction Method</Label>
                  <Input
                    data-testid="destruction-method-input"
                    value={retireForm.media_destruction_method}
                    onChange={(e) => setRetireForm({ ...retireForm, media_destruction_method: e.target.value })}
                    placeholder="e.g. Physical shredding, degaussing, DBAN 3-pass wipe"
                    className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
                  />
                </div>
                <div>
                  <Label className="uppercase-label">Notes</Label>
                  <Textarea
                    data-testid="destruction-notes-input"
                    value={retireForm.media_destruction_notes}
                    onChange={(e) => setRetireForm({ ...retireForm, media_destruction_notes: e.target.value })}
                    rows={2}
                    placeholder="Technician name, certificate ID, disposal vendor…"
                    className="mt-1 rounded-none bg-white border-[#E5E0D5] font-mono text-sm"
                  />
                </div>
              </>
            )}

            {!retireForm.media_destroyed && (
              <div className="text-xs text-[#B45309] font-mono flex items-start gap-2 border border-[#E0B8A5] bg-[#FFF4EE] px-3 py-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>The asset will be retired but the destruction step will remain <strong>unconfirmed</strong>. You can record it later using the same button.</span>
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="retire-cancel-btn" className="rounded-none border-[#D6CFC1] bg-transparent text-[#3A3A3A] hover:bg-[#E8E3D6]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="retire-confirm-btn"
              disabled={busy}
              onClick={(e) => { e.preventDefault(); onSubmitRetire(); }}
              className="rounded-none bg-[#B45309] hover:bg-[#96450A] text-white disabled:opacity-50"
            >{busy ? "Saving…" : (isRetired ? "Save destruction record" : "Retire asset")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div>
      <div className="uppercase-label">{label}</div>
      <div className={`mt-1 ${mono ? "font-mono text-xs" : "text-sm"} text-[#1F1F1F]`}>{value || "—"}</div>
    </div>
  );
}
